import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Alert,
  SelectChangeEvent
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { fetchProductLines, ProductLine } from 'api/productLines'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { BatchColorFailedItem } from 'hooks/useCart'

interface BatchEditProps {
  selectedItems: string[]
  notFoundItems: BatchColorFailedItem[]
  outOfStockItems: BatchColorFailedItem[]
  onBatchUpdate: (skuPrefix: string) => void
  onCancel: () => void
}

const BatchEdit: React.FC<BatchEditProps> = ({
  selectedItems,
  notFoundItems,
  outOfStockItems,
  onBatchUpdate,
  onCancel
}) => {
  const { t } = useTranslation()
  const [selectedSkuPrefix, setSelectedSkuPrefix] = useState('')
  const [productLines, setProductLines] = useState<ProductLine[]>([])
  const { showMessage } = useContext(SnackbarContext)

  useEffect(() => {
    fetchProductLines().then(setProductLines).catch(console.error)
  }, [])

  const handleBatchColorChange = (e: SelectChangeEvent<string>) => {
    const skuPrefix = e.target.value
    setSelectedSkuPrefix(skuPrefix)

    if (selectedItems.length === 0) {
      showMessage(t('snackbar.general.selectItems'))
      return
    }

    onBatchUpdate(skuPrefix)
    setSelectedSkuPrefix('')
  }

  return (
    <Box
      sx={{
        position: 'relative',
        p: 2,
        bgcolor: '#F8F8F8',
        borderRadius: 2,
        my: 3
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}
      >
        <Typography variant='h3'>{t('batchEdit.title')}</Typography>
        <IconButton size='small' onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Box>

      <FormControl fullWidth variant='outlined'>
        <Select
          displayEmpty
          value={selectedSkuPrefix}
          onChange={handleBatchColorChange}
          renderValue={(selected: string) =>
            selected ? (
              productLines.find(style => style.value === selected)?.label ||
              selected
            ) : (
              <em>{t('batchEdit.selectCabinetStyle')}</em>
            )
          }
          sx={{
            '& .MuiSelect-select:empty': {
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '16.94px',
              textAlign: 'left'
            }
          }}
        >
          <MenuItem value='' disabled>
            <em>{t('batchEdit.selectCabinetStyle')}</em>
          </MenuItem>
          {productLines.map(style => (
            <MenuItem key={style.value} value={style.value}>
              {style.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {notFoundItems.length > 0 && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('batchEdit.notFoundTitle')}
          </Typography>
          <Box component='ul' sx={{ m: 0, pl: 2 }}>
            {notFoundItems.map(item => (
              <li key={item.cartItemId}>{item.sku}</li>
            ))}
          </Box>
        </Alert>
      )}

      {outOfStockItems.length > 0 && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('batchEdit.outOfStockTitle')}
          </Typography>
          <Box component='ul' sx={{ m: 0, pl: 2 }}>
            {outOfStockItems.map(item => (
              <li key={item.cartItemId}>{item.sku}</li>
            ))}
          </Box>
        </Alert>
      )}
    </Box>
  )
}

export default BatchEdit
